import {
  Breakpoints,
  type ButtonProps,
  formatDate,
  formatName,
  formatTime,
  MINUTE,
  ModalContent,
  ModalHeight,
  ModalSize,
  ProfileImage,
  Rating,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { type FC, type PropsWithChildren } from 'react';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';
import { AddToCalendarButton } from '../../../coaching-session/AddToCalendarButton';
import { DropdownAlignment } from '../../../common/ActionsMenu';
import { SessionOption } from '../ScheduleModal';

import {
  type AdditionalAction,
  ScheduleModalFooter,
} from './ScheduleModalFooter';
import { ScheduleModalSidebar } from './ScheduleModalSidebar';
import {
  type BaseScheduleModalLayoutCoachFragment,
  type BaseScheduleModalLayoutCoachingSessionFragment,
  type BaseScheduleModalLayoutOrderFragment,
} from './__generated-gql-types__/BaseScheduleModalLayout.generated';

export interface BaseScheduleModalLayoutProps {
  additionalActions?: Possible<AdditionalAction[]>;
  buttonProps?: Partial<ButtonProps>;
  coach: BaseScheduleModalLayoutCoachFragment;
  coachingSession?: Possible<BaseScheduleModalLayoutCoachingSessionFragment>;
  customSidebar?: {
    title: string;
    subtitle: string;
    showAddToCalendar?: boolean;
  };
  hasUnlimitedBookingPower: boolean;
  hideSidebar?: boolean;
  isReschedule?: boolean;
  order: Possible<BaseScheduleModalLayoutOrderFragment>;
  preventScroll?: boolean;
  selectedTime: Possible<number>;
  sessionLength: number;
  sessionPrice: number;
  sessionType?: SessionOption;
  setOpen: (open: boolean) => void;
  setSelectedTime: (time: Possible<number>) => void;
  setSessionLength?: (length: number) => void;
  setSessionType?: (type: SessionOption) => void;
  sidebarLocked?: boolean;
  onBackClick?: () => void;
  onBackToStart: () => void;
  showSelectedTime?: boolean;
  showBackButton?: boolean;
  scheduledSessionId?: Scalars['CoachingSessionUrn']['output'];
}

export const BaseScheduleModalLayout: FC<
  PropsWithChildren<BaseScheduleModalLayoutProps>
> = ({
  additionalActions,
  buttonProps,
  children,
  coach,
  coachingSession,
  customSidebar,
  hasUnlimitedBookingPower,
  hideSidebar,
  isReschedule,
  order,
  selectedTime,
  sessionLength,
  sessionType,
  setOpen,
  setSelectedTime,
  setSessionLength,
  setSessionType,
  sidebarLocked,
  preventScroll,
  sessionPrice,
  onBackClick,
  onBackToStart,
  showSelectedTime = false,
  showBackButton = true,
  scheduledSessionId,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  return (
    <ModalContent
      hideCloseButton
      size={ModalSize.LARGE}
      height={ModalHeight.SMALL}
      footer={
        <ScheduleModalFooter
          buttonProps={buttonProps}
          additionalActions={additionalActions}
          onBackClick={onBackClick}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          showSelectedTime={showSelectedTime}
          showBackButton={showBackButton}
        />
      }
      preventScroll={preventScroll}
      useFullSpace
    >
      <div className="flex grow flex-col overflow-hidden">
        <div className="flex max-h-screen flex-col overflow-scroll overscroll-contain md:grow md:flex-row md:overflow-hidden">
          <div className="size-full max-w-115 shrink md:w-2/5 md:shrink-0">
            {customSidebar ? (
              <div className="hidden size-full border-r p-10 sm:block">
                <h2 className="text-3xl font-medium">{customSidebar.title}</h2>
                <p className="mt-4 text-lg text-leland-gray-light">
                  {customSidebar.subtitle}
                </p>
                {isMd && selectedTime ? (
                  <div className="mt-6">
                    <SidebarSessionCard
                      selectedTime={selectedTime}
                      sessionLength={sessionLength}
                      sessionType={sessionType}
                      coach={coach}
                      onBackToStart={onBackToStart}
                    />
                  </div>
                ) : null}
                {customSidebar.showAddToCalendar && scheduledSessionId ? (
                  <div className="mt-4 w-fit">
                    <AddToCalendarButton
                      scheduledSessionIds={[scheduledSessionId]}
                      dropdownAlignment={DropdownAlignment.LEFT}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <ScheduleModalSidebar
                hasUnlimitedBookingPower={hasUnlimitedBookingPower}
                hidden={hideSidebar}
                coach={coach}
                coachingSession={coachingSession}
                onChangeSessionLength={
                  setSessionLength
                    ? (value) => {
                        setSelectedTime(null);
                        setSessionLength(value);
                      }
                    : undefined
                }
                onCloseModal={setOpen}
                order={order}
                sessionLength={sessionLength}
                sessionType={sessionType}
                setSessionType={setSessionType}
                isReschedule={isReschedule}
                locked={sidebarLocked}
                sessionPrice={sessionPrice}
              />
            )}
          </div>
          <div className="flex w-full grow">{children}</div>
        </div>
      </div>
    </ModalContent>
  );
};

interface SidebarSessionCardProps {
  selectedTime: number;
  sessionLength: number;
  sessionType?: SessionOption;
  coach: BaseScheduleModalLayoutCoachFragment;
  onBackToStart: () => void;
}

const SidebarSessionCard: FC<SidebarSessionCardProps> = ({
  selectedTime,
  sessionLength,
  sessionType,
  coach,
  onBackToStart,
}) => {
  const date = formatDate(selectedTime);
  const timeRange = `${formatTime(selectedTime, { excludeTimeZone: true })} - ${formatTime(
    selectedTime + sessionLength * MINUTE,
    { excludeTimeZone: true },
  )}`;

  return (
    <div className="flex flex-col rounded-lg border">
      <div className="flex flex-row justify-between bg-leland-gray-hover p-2 text-leland-gray-light">
        <p>Your session</p>
        <button onClick={onBackToStart} className="link underline">
          Edit
        </button>
      </div>
      <div className="flex flex-col gap-1 p-3">
        <p className="font-medium text-leland-gray-dark">
          {sessionType === SessionOption.INTRO_CALL
            ? 'Free intro call'
            : 'Coaching session'}
        </p>
        <p className="text-leland-gray-light">{`${date} from ${timeRange}`}</p>
        <hr className="my-2" />
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            <ProfileImage className="size-5" {...coach.user} />
            <p>{`with ${formatName({ firstName: coach.user.firstName, lastName: coach.user.lastName })}`}</p>
          </div>
          {coach.reviewTotal > 0 ? (
            <div className="flex flex-row">
              <Rating totalStars={1} rate={coach.averageReviewRate} showRate />
              <p className="ml-1 text-lg text-leland-gray-extra-light">
                {`(${coach.reviewTotal})`}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
